import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatCurrency } from '../../utils/pricing';

/**
 * Meesho-style pricing formula (seller's own model):
 *
 *              (1-r)·C + k + r·R + (1-r)·S_eff + F
 *   Price = ─────────────────────────────────────────
 *                   (1-r) · (1 - m - f)
 *
 *   C  product cost          k  packing per order
 *   r  return rate           R  cost of one return (both shipping legs)
 *   m  profit % (of collected revenue)
 *   f  optional % fees       F  optional flat fees per order
 *   S_eff = our shipping cost, but ONLY when the price qualifies the
 *           customer for free shipping (>= threshold). Below it, the
 *           customer pays the delivery charge and S_eff = 0.
 *
 * Revenue is only collected on delivered orders (1-r), which is why it
 * appears in the denominator. Returned orders recover the product but
 * burn R; packing k is spent on every order.
 */
interface Assumptions {
  packing: number; // k
  returnRate: number; // r %
  returnCost: number; // R
  shipping: number; // S (our cost when free shipping applies)
  freeShipThreshold: number; // customer gets free shipping at/above this
  customerDelivery: number; // what the customer pays below the threshold
  feesPct: number; // f % (COD/GST/gateway if you want them)
  feesFlat: number; // F flat per order
}

const DEFAULTS: Assumptions = {
  packing: 5,
  returnRate: 15,
  returnCost: 160,
  shipping: 75,
  freeShipThreshold: 299,
  customerDelivery: 79,
  feesPct: 0,
  feesFlat: 0
};

const model = (a: Assumptions) => {
  const r = a.returnRate / 100;
  const f = a.feesPct / 100;

  const numerator = (cost: number, sEff: number) =>
    (1 - r) * cost + a.packing + r * a.returnCost + (1 - r) * sEff + a.feesFlat;

  return {
    /** price for m% profit, given whether we absorb shipping */
    priceForMargin: (m: number, cost: number, freeShip: boolean) => {
      const denom = (1 - r) * (1 - m / 100 - f);
      if (denom <= 0) return NaN;
      return numerator(cost, freeShip ? a.shipping : 0) / denom;
    },
    /** price for a target profit in rupees */
    priceForProfit: (t: number, cost: number, freeShip: boolean) => {
      const denom = (1 - r) * (1 - f);
      return (t + numerator(cost, freeShip ? a.shipping : 0)) / denom;
    },
    /** expected profit per order at price P */
    profitAt: (P: number, cost: number) => {
      const freeShip = P >= a.freeShipThreshold;
      return (1 - r) * P * (1 - f) - numerator(cost, freeShip ? a.shipping : 0);
    }
  };
};

/** Round UP to the nearest attractive 9-ending price (149, 199, 249...) */
const toCharmPrice = (p: number): number => {
  if (!Number.isFinite(p) || p <= 0) return 0;
  const step = p < 1000 ? 50 : 100;
  return Math.ceil((p + 1) / step) * step - 1;
};

const num = (v: string) => (v === '' ? 0 : Number(v));

interface Suggestion {
  price: number;
  freeShip: boolean;
  note: string;
}

const AdminPricingCalculatorPage: React.FC = () => {
  const [cost, setCost] = useState('40');
  const [mode, setMode] = useState<'margin' | 'profit'>('margin');
  const [targetMargin, setTargetMargin] = useState('11');
  const [targetProfit, setTargetProfit] = useState('34');
  const [checkPrice, setCheckPrice] = useState('');
  const [a, setA] = useState<Assumptions>(DEFAULTS);

  const m = useMemo(() => model(a), [a]);
  const purchaseCost = num(cost);

  const solve = (freeShip: boolean) =>
    mode === 'margin'
      ? m.priceForMargin(num(targetMargin), purchaseCost, freeShip)
      : m.priceForProfit(num(targetProfit), purchaseCost, freeShip);

  /**
   * The free-shipping threshold splits pricing into two self-consistent
   * branches; in the gap between them both options are valid and we show
   * the trade-off.
   */
  const suggestions: Suggestion[] = useMemo(() => {
    const pPaid = solve(false); // customer pays delivery (price < threshold)
    const pFree = solve(true); // we absorb shipping (price >= threshold)
    const out: Suggestion[] = [];

    const paidValid = Number.isFinite(pPaid) && toCharmPrice(pPaid) < a.freeShipThreshold;
    const freeValid = Number.isFinite(pFree);

    if (paidValid) {
      out.push({
        price: toCharmPrice(pPaid),
        freeShip: false,
        note: `Customer also pays ${formatCurrency(a.customerDelivery)} delivery at checkout`
      });
    }
    if (freeValid) {
      const price = Math.max(toCharmPrice(pFree), toCharmPrice(a.freeShipThreshold));
      out.push({
        price,
        freeShip: true,
        note: 'Free delivery for the customer — shipping cost is built into the price'
      });
    }
    return out;
  }, [a, mode, targetMargin, targetProfit, purchaseCost]); // eslint-disable-line react-hooks/exhaustive-deps

  const primary = suggestions[0];
  const priceToCheck = checkPrice ? num(checkPrice) : primary?.price || 0;

  const b = useMemo(() => {
    const P = priceToCheck;
    const r = a.returnRate / 100;
    const freeShip = P >= a.freeShipThreshold;
    const profit = m.profitAt(P, purchaseCost);
    const collected = (1 - r) * P;
    return {
      freeShip,
      collected,
      packing: a.packing,
      returnDrag: r * a.returnCost,
      shipCost: freeShip ? (1 - r) * a.shipping : 0,
      productCost: (1 - r) * purchaseCost,
      profit,
      marginPct: collected > 0 ? (profit / collected) * 100 : 0
    };
  }, [priceToCheck, a, m, purchaseCost]);

  const setField = (field: keyof Assumptions) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setA((prev) => ({ ...prev, [field]: num(e.target.value) }));

  const ladder = (mode === 'margin' ? [8, 11, 15, 20] : [20, 34, 50, 75]).map((t) => {
    const raw =
      mode === 'margin'
        ? m.priceForMargin(t, purchaseCost, true)
        : m.priceForProfit(t, purchaseCost, true);
    const paidRaw =
      mode === 'margin'
        ? m.priceForMargin(t, purchaseCost, false)
        : m.priceForProfit(t, purchaseCost, false);
    // Prefer the below-threshold branch when it's self-consistent
    const price =
      Number.isFinite(paidRaw) && toCharmPrice(paidRaw) < a.freeShipThreshold
        ? toCharmPrice(paidRaw)
        : Math.max(toCharmPrice(raw), toCharmPrice(a.freeShipThreshold));
    return {
      label: mode === 'margin' ? `${t}% profit` : `₹${t} profit`,
      price,
      profit: price > 0 ? m.profitAt(price, purchaseCost) : 0
    };
  });

  return (
    <Box sx={{ maxWidth: 980 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Pricing calculator
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Price = [(1−r)·C + k + r·R + shipping] ÷ [(1−r)·(1−m)] — with the free-shipping-above-
          {formatCurrency(a.freeShipThreshold)} rule applied
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' }, gap: 3 }}>
        {/* Inputs */}
        <Box>
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Product
            </Typography>
            <TextField
              fullWidth
              label="Purchase cost (C)"
              type="number"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              sx={{ mb: 2 }}
            />
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={mode}
              onChange={(_, v) => v && setMode(v)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="margin">Profit % (m)</ToggleButton>
              <ToggleButton value="profit">Profit ₹/order</ToggleButton>
            </ToggleButtonGroup>
            {mode === 'margin' ? (
              <TextField
                fullWidth
                label="Profit % of collected revenue"
                type="number"
                value={targetMargin}
                onChange={(e) => setTargetMargin(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            ) : (
              <TextField
                fullWidth
                label="Profit per order"
                type="number"
                value={targetProfit}
                onChange={(e) => setTargetProfit(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            )}
          </Paper>

          <Accordion variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Assumptions (k ₹{a.packing} · r {a.returnRate}% · R ₹{a.returnCost} · ship ₹
                {a.shipping} free ≥ {formatCurrency(a.freeShipThreshold)})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField size="small" label="Packing per order — k (₹)" type="number" value={a.packing} onChange={setField('packing')} />
                <TextField size="small" label="Return rate — r (%)" type="number" value={a.returnRate} onChange={setField('returnRate')} />
                <TextField size="small" label="Cost per return — R (₹)" type="number" value={a.returnCost} onChange={setField('returnCost')} />
                <TextField size="small" label="Our shipping cost (₹)" type="number" value={a.shipping} onChange={setField('shipping')} />
                <TextField size="small" label="Free shipping at/above (₹)" type="number" value={a.freeShipThreshold} onChange={setField('freeShipThreshold')} />
                <TextField size="small" label="Customer pays below it (₹)" type="number" value={a.customerDelivery} onChange={setField('customerDelivery')} />
                <TextField size="small" label="Extra fees — f (% of price)" type="number" value={a.feesPct} onChange={setField('feesPct')} helperText="Optional: COD/GST/gateway" />
                <TextField size="small" label="Extra flat fees — F (₹/order)" type="number" value={a.feesFlat} onChange={setField('feesFlat')} />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Results */}
        <Box>
          {suggestions.map((s, i) => (
            <Paper
              key={i}
              variant="outlined"
              sx={{
                p: 3,
                mb: 2,
                background:
                  i === 0 ? 'linear-gradient(135deg, rgba(214,90,136,0.06), #fff)' : undefined
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {i === 0 ? 'Suggested price' : 'Alternative'}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={s.freeShip ? 'success' : 'default'}
                  label={s.freeShip ? 'free shipping' : `+${formatCurrency(a.customerDelivery)} delivery`}
                  sx={{ height: 20 }}
                />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5 }}>
                {formatCurrency(s.price)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {s.note} · your expected profit ≈{' '}
                {formatCurrency(m.profitAt(s.price, purchaseCost))}/order
              </Typography>
            </Paper>
          ))}
          {suggestions.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              That target isn't reachable with these assumptions.
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Breakdown at {formatCurrency(priceToCheck || 0)}
                <Chip
                  size="small"
                  variant="outlined"
                  color={b.freeShip ? 'success' : 'default'}
                  label={b.freeShip ? 'free-ship zone' : 'customer pays delivery'}
                  sx={{ ml: 1, height: 20 }}
                />
              </Typography>
              <TextField
                size="small"
                label="Check another price"
                type="number"
                value={checkPrice}
                onChange={(e) => setCheckPrice(e.target.value)}
                sx={{ width: 170 }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Box>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Collected revenue — (1−r)·P, {100 - a.returnRate}% of orders deliver</TableCell>
                  <TableCell align="right">{formatCurrency(b.collected)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Product cost consumed — (1−r)·C</TableCell>
                  <TableCell align="right">−{formatCurrency(b.productCost)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Packing — k (every order)</TableCell>
                  <TableCell align="right">−{formatCurrency(b.packing)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Returns drag — r·R</TableCell>
                  <TableCell align="right">−{formatCurrency(b.returnDrag)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    Shipping we absorb {b.freeShip ? '— (1−r)·S' : '— none (customer pays delivery)'}
                  </TableCell>
                  <TableCell align="right">−{formatCurrency(b.shipCost)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Expected profit / order</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    <Box component="span" sx={{ color: b.profit >= 0 ? 'success.main' : 'error.main' }}>
                      {formatCurrency(b.profit)}
                    </Box>{' '}
                    <Chip
                      size="small"
                      label={`${b.marginPct.toFixed(1)}% of collected`}
                      color={b.profit >= 0 ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Price ladder for {formatCurrency(purchaseCost)} cost
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Target</TableCell>
                  <TableCell align="right">Sell at</TableCell>
                  <TableCell align="right">Profit / order</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ladder.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell align="right">{row.price > 0 ? formatCurrency(row.price) : '—'}</TableCell>
                    <TableCell align="right">{row.price > 0 ? formatCurrency(row.profit) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Prices are rounded up to 9-endings, so profit lands slightly above target. Suggestions
              near {formatCurrency(a.freeShipThreshold)} show both sides of the free-shipping rule.
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPricingCalculatorPage;
